import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { distinctUntilChanged, filter, map, Observable, of, take, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PropertySet } from '@miq/domain/property-sets';
import { PROPERTY_SETS_DIALOG_ACTIONS } from '../property-sets-dialog-actions/property-sets-dialog-actions';
import { PropertySetsStateProviderService } from '../property-set-list/property-set-list-state.service';
import { PropertySetDetailsDialogActionsService } from './property-set-dialog-actions.service';
import {
  PropertySetDetailsMode,
  PropertySetView,
  PropertySetsState,
} from '../property-sets-dialog/property-sets-dialog';
import { STATE_PROVIDER, StateProvider } from '../services/state-provider/state-provider';
import { PropertySetService } from './property-set.service';
import { PropetySetConfirmationDialogComponent } from './propety-set-confirmation-dialog/property-set-confirmation-dialog.component';
import { DialogService } from '@cbre/components';
import { CbreAlertService } from '@cbre/alerts';
import { User } from '@miq/user';

@UntilDestroy()
@Component({
  selector: 'miq-property-set',
  template: `
    <!-- actions -->
    <miq-property-sets-dialog-actions></miq-property-sets-dialog-actions>
    <ng-container *ngIf="propertySet$ | async as propertySet">
      <div *ngIf="mode$ | async as mode" class="fx--padding-20 fx--pt-xs fx--v ps-properties-dialog-content">
        <!-- MAIN PS INFO -->
        <!-- FORM -->

        <ng-container *ngIf="mode === MODE.Edit; else psSummary">
          <miq-property-set-form [propertySet]="propertySet" [user]="user" class="fx--mb-sm"></miq-property-set-form>
        </ng-container>

        <!-- SUMMARY (DEFAULT) -->
        <ng-template #psSummary>
          <miq-property-set-summary [propertySet]="propertySet" [user]="user"></miq-property-set-summary>
        </ng-template>

        <!-- RECORDS & PERMISSIONS TABS -->
        <div class="fx--h fx--mt-sm ps-properties-dialog__tabs">
          <div class="tab-header-container">
            <mat-tab-group mat-stretch-tabs="false" disableRipple>
              <mat-tab label="Properties" [disabled]="isRecordsPermissionDisabled">
                <miq-property-set-records [propertySet]="propertySet"> </miq-property-set-records>
              </mat-tab>
              <mat-tab label="Historical Detail" *ngIf="showDownloadTab" [disabled]="isRecordsPermissionDisabled">
                <miq-historical-detail [propertySet]="propertySet"></miq-historical-detail>
              </mat-tab>
              <mat-tab label="Sharing Details" [disabled]="isRecordsPermissionDisabled">
                <miq-permissions [propertySet]="propertySet"></miq-permissions>
              </mat-tab>
            </mat-tab-group>

            <button
              *ngIf="mode === MODE.Edit && propertySet.id !== -1"
              id="bulkEditButton"
              class="action-button txt-button enable-button"
              [matTooltip]="deleteTooltipText"
              matTooltipPosition="above"
              matTooltipClass="tooltip-action-icon"
              (click)="deleteSelectedPropertySet(propertySet.id)"
            >
              <mat-icon class="delete-icon" svgIcon="icon-trash"></mat-icon> {{ deleteButton }}
            </button>
          </div>
        </div>
      </div>
    </ng-container>
  `,
  providers: [
    PropertySetsStateProviderService,
    PropertySetDetailsDialogActionsService,
    PropertySetService,
    { provide: PROPERTY_SETS_DIALOG_ACTIONS, useClass: PropertySetDetailsDialogActionsService },
  ],
  styleUrls: ['./property-set.component.scss'],
  standalone: false,
})
export class PropertySetComponent implements OnInit {
  public readonly MODE = PropertySetDetailsMode;
  public isRecordsPermissionDisabled = false;
  public mode$: Observable<PropertySetDetailsMode> = null;
  public propertySet$: Observable<PropertySet> = null;
  public deleteButton = 'Delete';
  public deleteTooltipText = 'Permanently delete this Property Set';
  private readonly deleteMessage = 'Property Set Successfully Deleted';
  private readonly showTabRoles = [1, 3, 8]; // Roles that can see the download tab : US Researcher(1),DIG User(3) & D&T (8)
  public user: User;
  showDownloadTab = false;

  constructor(
    @Inject(STATE_PROVIDER) private propertySetsStateProvider: StateProvider<PropertySetsState>,
    private propertySetService: PropertySetService,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService,
    private cbreAlertService: CbreAlertService
  ) {}

  ngOnInit(): void {
    this.user = this.propertySetsStateProvider.snapshot().userInfo;
    this.showDownloadTab = this.showTabRoles.includes(this.user?.roleId);
    this._loadPropertySet();

    // Listen to mode changes
    this.mode$ = this.propertySetsStateProvider.stateChanges$.pipe(
      untilDestroyed(this),
      tap(state => (this.isRecordsPermissionDisabled = state.id === -1)),
      map(state => state.mode),
      distinctUntilChanged()
    );

    // Listen to propertySet changes
    this.propertySet$ = this.propertySetsStateProvider.stateChanges$.pipe(
      untilDestroyed(this),
      filter(Boolean),
      map(s => s.propertySet),
      distinctUntilChanged()
    );

    this.cdr.detectChanges();
  }

  private _loadPropertySet = (): void => {
    const id = this.propertySetsStateProvider.snapshot().id;
    const isNewPS = id === -1;

    const mode = isNewPS ? PropertySetDetailsMode.Edit : PropertySetDetailsMode.Read;
    const source$ = isNewPS ? of({ id, name: '' }) : this.propertySetService.fetch(id);
    source$.pipe(take(1)).subscribe(ps =>
      this.propertySetsStateProvider.patchState({
        id,
        mode,
        propertySet: ps,
      })
    );
  };

  public deleteSelectedPropertySet(id: number): void {
    const dialogConfig = {
      width: '60vw',
      panelClass: ['alert-modals'],
      height: 'auto',
      position: { top: '15vh', left: '20vw' },
    };
    const dialogData = { propertySetId: id };
    this.dialogService.openDialog(PropetySetConfirmationDialogComponent, dialogData, dialogConfig).subscribe(result => {
      if (result) {
        this.propertySetsStateProvider.patchState({
          view: PropertySetView.List,
          id: null,
          hasBackButton: false,
          propertySet: null,
          propertySetChange: null,
        });
        this.cbreAlertService.showSuccessSnackBar_l10n(this.deleteMessage);
      }
    });
  }
}
